import React, { JSX, useEffect } from 'react';
import { Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet.fullscreen';
import L, { LatLng, LatLngExpression } from 'leaflet';
import { Marker as MarkerType } from './MapsInterfaces';
import { wait } from '../../Util/utils';
import Keys from '../../Translations/generated/park-map.en.json.keys';
import { useTranslation } from 'react-i18next';
import { Price } from '@ibe/components';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { LeafletMapProps } from './LeafletMapProps';

const icon =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAApCAYAAADAk4LOAAAFgUlEQVR4Aa1XA5BjWRTN2oW17d3YaZtr2962HUzbDNpjszW24mRt28p47v7zq/bXZtrp/lWnXr337j3nPCe85NcypgSFdugCpW5YoDAMRaIMqRi6aKq5E3YqDQO3qAwjVWrD8Ncq/RBpykd8oZUb/kaJutow8r1aP9II0WmLKLIsJyv1w/kqw9Ch2MYdB++12Onxee/QMwvf4/Dk/Lfp/i4nxTXtOoQ4pW5Aj7wpici1A9erdAN2OH64x8OSP9j3Ft3b7aWkTg/Fm91siTra0f9on5sQr9INejH6CUUUpavjFNq1B+Oadhxmnfa8RfEmN8VNAsQhPqF55xHkMzz3jSmChWU6f7/XZKNH+9+hBLOHYozuKQPxyMPUKkrX/K0uWnfFaJGS1QPRtZsOPtr3NsW0uyh6NNCOkU3Yz+bXbT3I8G3xE5EXLXtCXbbqwCO9zPQYPRTZ5vIDXD7U+w7rFDEoUUf7ibHIR4y6bLVPXrz8JVZEql13trxwue/uDivd3fkWRbS6/IA2bID4uk0UpF1N8qLlbBlXs4Ee7HLTfV1j54APvODnSfOWBqtKVvjgLKzF5YdEk5ewRkGlK0i33Eofffc7HT56jD7/6U+qH3Cx7SBLNntH5YIPvODnyfIXZYRVDPqgHtLs5ABHD3YzLuespb7t79FY34DjMwrVrcTuwlT55YMPvOBnRrJ4VXTdNnYug5ucHLBjEpt30701A3Ts+HEa73u6dT3FNWwflY86eMHPk+Yu+i6pzUpRrW7SNDg5JHR4KapmM5Wv2E8Tfcb1HoqqHMHU+uWDD7zg54mz5/2BSnizi9T1Dg4QQXLToGNCkb6tb1NU+QAlGr1++eADrzhn/u8Q2YZhQVlZ5+CAOtqfbhmaUCS1ezNFVm2imDbPmPng5wmz+gwh+oHDce0eUtQ6OGDIyR0uUhUsoO3vfDmmgOezH0mZN59x7MBi++WDL1g/eEiU3avlidO671bkLfwbw5XV2P8Pzo0ydy4t2/0eu33xYSOMOD8hTf4CrBtGMSoXfPLchX+J0ruSePw3LZeK0juPJbYzrhkH0io7B3k164hiGvawhOKMLkrQLyVpZg8rHFW7E2uHOL888IBPlNZ1FPzstSJM694fWr6RwpvcJK60+0HCILTBzZLFNdtAzJaohze60T8qBzyh5ZuOg5e7uwQppofEmf2++DYvmySqGBuKaicF1blQjhuHdvCIMvp8whTTfZzI7RldpwtSzL+F1+wkdZ2TBOW2gIF88PBTzD/gpeREAMEbxnJcaJHNHrpzji0gQCS6hdkEeYt9DF/2qPcEC8RM28Hwmr3sdNyht00byAut2k3gufWNtgtOEOFGUwcXWNDbdNbpgBGxEvKkOQsxivJx33iow0Vw5S6SVTrpVq11ysA2Rp7gTfPfktc6zhtXBBC+adRLshf6sG2RfHPZ5EAc4sVZ83yCN00Fk/4kggu40ZTvIEm5g24qtU4KjBrx/BTTH8ifVASAG7gKrnWxJDcU7x8X6Ecczhm3o6YicvsLXWfh3Ch1W0k8x0nXF+0fFxgt4phz8QvypiwCCFKMqXCnqXExjq10beH+UUA7+nG6mdG/Pu0f3LgFcGrl2s0kNNjpmoJ9o4B29CMO8dMT4Q5ox8uitF6fqsrJOr8qnwNbRzv6hSnG5wP+64C7h9lp30hKNtKdWjtdkbuPA19nJ7Tz3zR/ibgARbhb4AlhavcBebmTHcFl2fvYEnW0ox9xMxKBS8btJ+KiEbq9zA4RthQXDhPa0T9TEe69gWupwc6uBUphquXgf+/FrIjweHQS4/pduMe5ERUMHUd9xv8ZR98CxkS4F2n3EUrUZ10EYNw7BWm9x1GiPssi3GgiGRDKWRYZfXlON+dfNbM+GgIwYdwAAAAASUVORK5CYII=';

const defaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: icon,
  iconAnchor: [5, 55],
  popupAnchor: [13, -44],
  iconSize: [35, 45]
});

const reorderMapControls = (map: L.Map): void => {
  const container = map.getContainer();

  // The corner that holds attribution stays at the end of the DOM.
  const attributionCorner = container
    .querySelector('.leaflet-control-attribution')
    ?.closest('.leaflet-top, .leaflet-bottom');

  const corners = container.querySelectorAll(
    ':scope > .leaflet-control-container > .leaflet-top, ' +
      ':scope > .leaflet-control-container > .leaflet-bottom'
  );

  // Insert before the first pane so relative corner order is preserved.
  const firstPane = container.querySelector(':scope > .leaflet-pane');

  corners.forEach(corner => {
    if (corner === attributionCorner) return;
    firstPane?.before(corner);
  });
};

const LeafletMapComponent = (props: LeafletMapProps): JSX.Element => {
  const {
    containerRef,
    markers,
    bounds,
    centerMap,
    innerMapRef,
    handleHotelClick,
    centerMapRadius
  } = props;
  const map = useMap();
  const { t } = useTranslation('park-map');

  useEffect(() => {
    if (map) {
      reorderMapControls(map);

      innerMapRef.current = map;
    }
  }, [map]);

  const applyBounds = () => {
    if (!bounds?.isValid()) {
      return;
    }

    if (centerMap) {
      const center = L.latLng(centerMap.lat, centerMap.lng);
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      let latDiff = Math.max(Math.abs(center.lat - ne.lat), Math.abs(center.lat - sw.lat));
      let lngDiff = Math.max(Math.abs(center.lng - ne.lng), Math.abs(center.lng - sw.lng));

      if (centerMapRadius) {
        // Narrow view to given radius
        const cap = center.toBounds(centerMapRadius * 1000 * 2);
        const capLat = cap.getNorth() - center.lat;
        const capLng = cap.getEast() - center.lng;

        latDiff = Math.min(latDiff, capLat);
        lngDiff = Math.min(lngDiff, capLng);
      }
      const symmetricalBounds = L.latLngBounds(
        [center.lat - latDiff, center.lng - lngDiff],
        [center.lat + latDiff, center.lng + lngDiff]
      );
      const targetZoom = map.getBoundsZoom(symmetricalBounds);
      map.setView([center.lat, center.lng], targetZoom, { animate: false });
    } else {
      map.fitBounds(bounds, { animate: false });
    }
  };

  const createdCallBack = async (iterations: number): Promise<void> => {
    if (
      (containerRef.current && containerRef.current.clientHeight > 0 && !!map) ||
      iterations === 0
    ) {
      map.invalidateSize();
      if (bounds?.isValid() && !!map.getPanes()?.mapPane) {
        applyBounds();

        map.addControl(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (L.control as any).fullscreen({
            forceSeparateButton: true,
            title: t(Keys.toggleFullscreenView),
            titleCancel: t(Keys.exitFullscreenView)
          })
        );
      }
      map.on('enterFullscreen', function () {
        setTimeout(() => {
          if (map.getPanes()?.mapPane) {
            applyBounds();
          }
        }, 500);
      });
      map.on('exitFullscreen', function () {
        setTimeout(() => {
          applyBounds();
        }, 500);
      });
    } else {
      createdCallBack(iterations - 1).catch(error_ => console.warn(error_));
    }
  };

  useEffect(() => {
    createdCallBack(20).catch(error_ => console.warn(error_));
  }, []);

  const getMarker = (marker: MarkerType): LatLngExpression => {
    return new LatLng(marker.geoData!.latitude, marker.geoData!.longitude);
  };

  const getIcon = (marker: MarkerType): L.Icon<L.IconOptions> => {
    return marker.iconUrl
      ? L.icon({
          iconUrl: marker.iconUrl,
          iconRetinaUrl: marker.iconUrl,
          iconAnchor: [5, 55],
          popupAnchor: [18, -45],
          iconSize: [45, 60]
        })
      : defaultIcon;
  };

  return (
    <>
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {markers?.map((marker): JSX.Element | undefined => {
        const hasGeoData = marker.geoData !== undefined;
        return hasGeoData ? (
          <Marker
            position={getMarker(marker)}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            icon={getIcon(marker) as any}
            key={`${marker.name}${marker?.geoData?.latitude}${marker?.geoData?.longitude}`}
            keyboard={true}
            alt={marker.name || 'Map marker'}
          >
            <Popup
              className="map-marker"
              keepInView={false}
              autoPan={true}
              autoClose={false}
              autoPanPaddingTopLeft={[0, 90]}
              eventHandlers={{
                add: e => {
                  const attemptFocus = (retries: number) => {
                    const popupElement = e.target.getElement();
                    if (popupElement) {
                      const container = popupElement.querySelector(
                        '.map-marker__popup'
                      ) as HTMLElement;
                      if (container && document.contains(container)) {
                        container.setAttribute('tabindex', '-1');
                        container.setAttribute('role', 'dialog');
                        container.setAttribute(
                          'aria-label',
                          `${t(Keys.ariaDetailsPrefix)} ${marker.name}`
                        );
                        container.focus();
                        if (document.activeElement === container) {
                          return;
                        }
                      }
                    }
                    if (retries > 0) {
                      setTimeout(() => attemptFocus(retries - 1), 100);
                    }
                  };
                  setTimeout(() => attemptFocus(5), 300);
                },
                remove: e => {
                  // eslint-disable-next-line no-underscore-dangle
                  const markerElement = e.target._source?.getElement() as HTMLElement;
                  if (markerElement) {
                    setTimeout(() => markerElement.focus(), 100);
                  }
                }
              }}
            >
              <div
                className="map-marker__popup"
                role="dialog"
                aria-label={`${t(Keys.ariaDetailsPrefix)} ${marker.name}`}
                aria-modal="false"
                tabIndex={-1}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    map.closePopup();
                  }
                }}
              >
                {marker.image?.small?.url && (
                  <img
                    src={marker.image?.small?.url}
                    alt={marker.image?.imageAlt ?? `Image of ${marker.name}`}
                  />
                )}
                <div
                  className={`map-marker__popup__name ${
                    marker.image?.small?.url ? '' : 'top-placed'
                  }`}
                >
                  {marker.name}
                </div>
                {marker.distance?.description && (
                  <div className={'map-marker__popup__location'}>{`${t(Keys.location)}: ${
                    marker.distance?.description
                  }`}</div>
                )}
                <Price
                  price={marker.price}
                  displayInline
                  className="map-marker__popup__price"
                  prefix={t(Keys.from)}
                />
                {!marker.isPark && marker.id && handleHotelClick && (
                  <Button
                    className="map-marker__popup__button"
                    aria-label={`${t(Keys.showHotel)} - ${marker.name}`}
                    onClick={() => handleHotelClick(marker.id as string)}
                  >
                    {t(Keys.showHotel)}
                    <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                  </Button>
                )}
              </div>
            </Popup>
          </Marker>
        ) : undefined;
      })}
    </>
  );
};

export default LeafletMapComponent;
